import { useRef, useState } from 'react';
import PupCardPreview from '../pupCards/PupCardPreview';
import './LogLine.css';

// A drift beyond this many px cancels a press (neither a click nor a hold) — mirrors PupHand.js.
const DRAG_THRESHOLD_PX = 6;
// How long a press must be held before it becomes a "hold" (shows the preview) instead of a click.
export const HOLD_MS = 180;

// Renders ONE formatted log line (a descriptor from actionLogFormatter) to DOM. Shared by the
// ActionLog sidebar and the replay window so segment rendering + Pup-name interaction live in one
// place. The formatter is the single source of the line's text/segments; this only maps segments to
// elements. A Pup name supports TWO gestures: a plain click opens the card-detail modal
// (`onPupClick(code)`, optional), and press-and-hold shows a transient full-card preview until
// release. Keyboard (Enter/Space) still opens the modal. `roomId` rides through to the preview so
// table-type-dependent card text (the tournament removal note) renders correctly.
const LogLine = ({ line, onPupClick, roomId }) => {
  const [previewCode, setPreviewCode] = useState(null);
  // Refs (not state) — gesture bookkeeping must not trigger re-renders. Only one Pup name can be
  // pressed at a time even on a line with several, so a single set per LogLine is sufficient.
  const holdTimer = useRef(null);
  const holdFired = useRef(false);
  const cancelled = useRef(false);
  const pointerHandled = useRef(false);
  const start = useRef({ x: 0, y: 0 });

  const clearHold = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const onPointerDown = (e, code) => {
    // Left button / primary pointer only. Tolerate undefined (mouse defaults) so this works under
    // jsdom too; still reject an explicit right/middle click or a secondary touch point.
    if ((e.button ?? 0) !== 0 || e.isPrimary === false) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* jsdom / unsupported */ }
    e.preventDefault(); // suppress native text-drag / selection
    pointerHandled.current = true;
    holdFired.current = false;
    cancelled.current = false;
    start.current = { x: e.clientX, y: e.clientY };
    clearHold();
    holdTimer.current = setTimeout(() => {
      holdFired.current = true;
      setPreviewCode(code);
    }, HOLD_MS);
  };

  const onPointerMove = (e) => {
    if (holdFired.current || cancelled.current) return; // capture keeps a fired preview steady
    const moved = Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y);
    if (moved > DRAG_THRESHOLD_PX) {
      clearHold();
      cancelled.current = true; // a drifting press is neither a click nor a hold
    }
  };

  const onPointerUp = (e, code) => {
    clearHold();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ignore */ }
    if (holdFired.current) {
      setPreviewCode(null); // hold consumed the gesture — do NOT open the modal
    } else if (!cancelled.current && onPupClick) {
      onPupClick(code); // quick tap opens the existing card-detail modal
    }
  };

  const onPointerCancel = () => {
    clearHold();
    cancelled.current = true;
    setPreviewCode(null);
  };

  // Swallow the mouse's trailing synthetic click after any pointer-driven gesture (the pointer path
  // already decided). With no preceding pointerdown this is the KEYBOARD path (Enter/Space on a
  // <button> dispatch a click), so the modal still opens — accessibility preserved.
  const onClick = (code) => {
    if (pointerHandled.current) {
      pointerHandled.current = false;
      return;
    }
    if (onPupClick) onPupClick(code);
  };

  const segments = line.segments.map((seg, i) => {
    if (seg.type === 'card') {
      return <span key={i} className="log-card" style={{ color: seg.color }}>{seg.text}</span>;
    }
    if (seg.type === 'pup') {
      return (
        <button
          key={i}
          type="button"
          className="log-pup-name"
          onPointerDown={(e) => onPointerDown(e, seg.code)}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => onPointerUp(e, seg.code)}
          onPointerCancel={onPointerCancel}
          onClick={() => onClick(seg.code)}
        >
          {seg.text}
        </button>
      );
    }
    return <span key={i}>{seg.text}</span>;
  });

  const row = line.kind === 'divider'
    ? <div className="log-row log-divider"><span className="log-divider-label">{segments}</span></div>
    : <div className={`log-row log-${line.category}`}>{segments}</div>;

  return (
    <>
      {row}
      {previewCode && <PupCardPreview code={previewCode} roomId={roomId} />}
    </>
  );
};

export default LogLine;
